import React, { useState, useEffect } from "react";
import {
  TextField,
  Autocomplete,
  Checkbox,
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
  IconButton,
  Button,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const schools = Array.from({ length: 1000 }, (_, i) => ({
  label: `School ${i + 1}`,
  id: i + 1,
}));

// Simple debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

export default function SchoolDropdown() {
  const [selectedSchools, setSelectedSchools] = useState<typeof schools>([]);
  const [inputValue, setInputValue] = useState("");
  const debouncedInput = useDebounce(inputValue, 1000); // 1 second debounce

  const navigate = useNavigate();
  const handleNext = (e: any) => {
    e.preventDefault();
    navigate("/profile");
  };

  // Compute available options (exclude selected ones)
  const availableOptions = schools.filter(
    (s) => !selectedSchools.some((sel) => sel.id === s.id)
  );

  return (
    <Box>
      {/* White panel */}
      <Paper elevation={2} sx={{ p: 2, bgcolor: "background.paper" }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={availableOptions}
          value={selectedSchools}
          inputValue={inputValue}
          onInputChange={(_, newValue) => setInputValue(newValue)}
          onChange={(_, newValue) => setSelectedSchools(newValue)}
          getOptionLabel={(option) => option.label}
          renderTags={() => null}
          filterOptions={(options) => {
            if (!debouncedInput) {
              // Show only first 10 when nothing typed
              return options.slice(0, 10);
            }
            const lower = debouncedInput.toLowerCase();
            return options.filter((o) =>
              o.label.toLowerCase().includes(lower)
            );
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Checkbox icon={icon} checkedIcon={checkedIcon} sx={{ mr: 1 }} />
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search schools"
              placeholder="Type to search..."
            />
          )}
        />

        {/* Bottom panel with selected schools */}
        <Box mt={2}>
          <List dense>
            {selectedSchools.map((s) => (
              <ListItem
                key={s.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="remove"
                    color="error" // makes the trash can red
                    size="small"  // keeps it compact
                    onClick={() =>
                      setSelectedSchools(selectedSchools.filter((sel) => sel.id !== s.id))
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText primary={s.label} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Paper>

      {/* Next button outside the panel */}
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button variant="contained" color="primary" onClick={handleNext}>
          Next
        </Button>
      </Box>
    </Box>
  );
}
